import { useEffect, useRef, useState } from 'react'
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer } from '@tiptap/react'
import type { NodeViewProps } from '@tiptap/react'
import CodeBlock from '@tiptap/extension-code-block'
import type { Mermaid } from 'mermaid'
import { useLang } from '../../i18n'
import { formatCodeLanguage, parseCodeLanguage } from '../../lib/diagram-geometry'
import { applyWidth, useWidthResize } from './width-resize'

/**
 * Mermaid is a heavy dependency, so it is code-split: the module is only fetched the first
 * time a diagram actually renders, and initialised exactly once.
 */
let mermaidPromise: Promise<Mermaid> | null = null
function getMermaid(): Promise<Mermaid> {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(({ default: mermaid }) => {
      mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'neutral', fontFamily: 'inherit' })
      return mermaid
    })
  }
  return mermaidPromise
}

/**
 * Node view for fenced code blocks. A ` ```mermaid ` block renders as the diagram (click
 * to edit the source, click away to re-render); any other code block stays an editable
 * `<pre><code>`. The CRDT is unchanged — it is still a fenced markdown code block, so the
 * diagram round-trips through markdown like any code.
 *
 * A diagram carries TWO percentages, and they are not the same thing:
 *  - the ZOOM, in the top-right tools, magnifies the drawing INSIDE the box. It is a view
 *    setting — private to one reader, not stored, gone on reload.
 *  - the WIDTH, in the bottom-right tools and on the four corner handles, is how much of the
 *    text column the box itself takes. It belongs to the document, so it is written into the
 *    fence's info string as a `=NN%` token (`lib/diagram-geometry.ts`) and survives a reload,
 *    a remote edit, a canonicalisation pass and an export.
 */
function MermaidNodeView({ node, editor, deleteNode, getPos, updateAttributes }: NodeViewProps) {
  const { t } = useLang()
  const { language: lang, widthPct } = parseCodeLanguage(node.attrs.language as string | null)
  const isMermaid = lang === 'mermaid'
  const isSvg = lang === 'svg'
  const isDiagram = isMermaid || isSvg
  const code = node.textContent

  // Start a freshly-inserted empty diagram in edit mode so the user can type.
  const [editing, setEditing] = useState(() => isDiagram && code.trim() === '')
  const [svg, setSvg] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const setZoomClamped = (z: number): void => setZoom(Math.min(4, Math.max(0.5, Math.round(z * 4) / 4)))
  const wrapperRef = useRef<HTMLDivElement>(null)

  /** Write ONE outer width into the fence's info string, and show what actually landed. */
  const commit = (pct: number | null): void => {
    updateAttributes({ language: formatCodeLanguage({ language: lang, widthPct: pct }) })
    // SectionNumbers' filterTransaction drops (and toasts) a write into a § someone else holds;
    // updateAttributes is an ordinary transaction, so it is refused like any other. Re-read what
    // actually landed rather than leaving the box showing a width the document does not have.
    const wrapper = wrapperRef.current
    if (!wrapper) return
    const pos = getPos()
    const live = pos == null ? null : editor.state.doc.nodeAt(pos)
    applyWidth(wrapper, parseCodeLanguage((live?.attrs.language as string | null) ?? null).widthPct)
  }

  // While the SOURCE editor is open the box goes back to the full column: editing markdown in a
  // 40%-wide gutter is miserable, and the stored width returns the moment the diagram does.
  const shownWidth = editing ? null : widthPct

  const { corners, tools } = useWidthResize({
    wrapperRef,
    widthPct: shownWidth,
    editable: editor.isEditable,
    commit,
    labels: {
      corner: {
        tl: t('diagram_resize_tl'),
        tr: t('diagram_resize_tr'),
        bl: t('diagram_resize_bl'),
        br: t('diagram_resize_br'),
      },
      field: t('diagram_width'),
      dec: t('diagram_width_dec'),
      inc: t('diagram_width_inc'),
    },
  })

  useEffect(() => {
    if (!isMermaid || editing) return
    const src = code.trim()
    if (!src) {
      setSvg('')
      setErr(null)
      return
    }
    let cancelled = false
    // A FRESH id per render — reusing one (e.g. after toggling source ↔ diagram) makes Mermaid
    // collide on the existing element and return an empty diagram.
    const id = 'mmd-' + Math.random().toString(36).slice(2, 10)
    getMermaid()
      .then((mermaid) => mermaid.render(id, src))
      .then(({ svg }) => {
        if (!cancelled) {
          setSvg(svg)
          setErr(null)
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Invalid Mermaid diagram')
      })
    return () => {
      cancelled = true
    }
  }, [code, isMermaid, editing])

  if (!isDiagram) {
    return (
      <NodeViewWrapper as="pre" className="cb">
        <NodeViewContent<'code'> as="code" />
      </NodeViewWrapper>
    )
  }

  // Rendered preview: Mermaid → compiled SVG; SVG block → the markup itself, shown via an
  // <img> data-URL so any embedded script can't run (safe, no DOM injection).
  const renderPreview = () => {
    const wstyle = { width: `${zoom * 100}%` }
    if (isSvg) {
      return code.trim() ? (
        <img className="diagram-img" alt="SVG" style={{ ...wstyle, maxWidth: 'none' }} src={`data:image/svg+xml,${encodeURIComponent(code)}`} />
      ) : (
        <p className="mermaid-empty">{t('svg_empty')}</p>
      )
    }
    if (err) return <pre className="mermaid-err" role="alert">⚠ {err}</pre>
    if (svg) return <div className="mermaid-svg" style={wstyle} dangerouslySetInnerHTML={{ __html: svg }} />
    return <p className="mermaid-empty">{t('mmd_empty')}</p>
  }

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      className={'mermaid-nv' + (shownWidth == null ? '' : ' sized')}
      onBlur={(e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEditing(false)
      }}
    >
      {!editing && (
        <>
          <div
            className="mermaid-view"
            role="button"
            tabIndex={0}
            title={t('mmd_edit_hint')}
            onClick={() => editor.isEditable && setEditing(true)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && editor.isEditable) {
                e.preventDefault()
                setEditing(true)
              }
            }}
          >
            <div className="mermaid-canvas">{renderPreview()}</div>
          </div>
          {/* EVERY control sits outside `.mermaid-view`, which is itself a role="button" that
              opens the source editor: a control nested in it would have its clicks and keypresses
              swallowed by that (Space on the zoom button used to open the editor too) and would
              fail axe's nested-interactive rule. They are pinned to the wrapper instead, which
              the box fills exactly, so nothing moves on screen. */}
          {/* Zoom — the INSIDE of the box. */}
          <span className="mermaid-tools">
            <button className="mermaid-tbtn" type="button" title={t('zoom_out')} aria-label={t('zoom_out')} disabled={zoom <= 0.5} onClick={() => setZoomClamped(zoom - 0.25)}>−</button>
            <button className="mermaid-tbtn mermaid-zoomlvl" type="button" title={t('zoom_reset')} aria-label={t('zoom_reset')} onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
            <button className="mermaid-tbtn" type="button" title={t('zoom_in')} aria-label={t('zoom_in')} disabled={zoom >= 4} onClick={() => setZoomClamped(zoom + 0.25)}>+</button>
            {editor.isEditable && (
              <button className="mermaid-tbtn mermaid-tbtn-del" type="button" title={t('diagram_delete')} aria-label={t('diagram_delete')} onClick={() => deleteNode()}>✕</button>
            )}
          </span>
          {/* Width — the OUTSIDE of the box. */}
          {corners}
          {tools && (
            <div className="dgm-size" role="group" aria-label={t('diagram_size_tools')}>
              {tools}
            </div>
          )}
        </>
      )}
      {/* Source editor — kept mounted (display toggled) so ProseMirror keeps managing the content. */}
      <div className="mermaid-edit" style={{ display: editing ? 'block' : 'none' }}>
        <div className="mermaid-edit-bar">
          <span className="mermaid-tag">{lang}</span>
          <span className="mermaid-bar-acts">
            <button
              type="button"
              className="mermaid-render-btn"
              title={t('mmd_render')}
              // Keep the caret; just leave edit mode → the effect re-renders the diagram.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setEditing(false)}
            >
              ▷ {t('mmd_render')}
            </button>
            <button
              type="button"
              className="mermaid-del"
              title={t('diagram_delete')}
              aria-label={t('diagram_delete')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => deleteNode()}
            >
              ✕
            </button>
          </span>
        </div>
        <pre className="cb mermaid-src" data-language={lang}>
          <NodeViewContent<'code'> as="code" />
        </pre>
      </div>
    </NodeViewWrapper>
  )
}

/** StarterKit's code block, upgraded with the Mermaid-aware node view. */
export const CodeBlockMermaid = CodeBlock.extend({
  addNodeView() {
    return ReactNodeViewRenderer(MermaidNodeView)
  },
})
